// Lee la hoja de la base consolidada de exportaciones (una fila por embarque)
// y la deja lista para stage_industry_volume_rows. La normalización
// (exportadora, transporte, semana ISO, equivalencias) la hace la base al
// cerrar la carga; aquí solo se ubican las columnas, se convierten fechas y
// números y se resume por temporada para la vista previa.

export type VolumeRow = {
  row_number: number;
  species: string | null;
  variety: string | null;
  boxes: number | null;
  kilos: number | null;
  exporter: string | null;
  destination_region: string | null;
  destination_port: string | null;
  destination_country: string | null;
  receiver: string | null;
  ship: string | null;
  ship_type: string | null;
  week: number | null;
  etd: string | null;
  origin_port: string | null;
  eta: string | null;
  origin_region_code: number | null;
  season: string | null;
};

type Field = Exclude<keyof VolumeRow, 'row_number'>;

// Encabezado del Excel → campo. Se comparan sin tildes, puntos ni espacios.
const HEADERS: Record<string, Field> = {
  ESPECIES: 'species',
  ESPECIE: 'species',
  VARIEDAD: 'variety',
  CAJAS: 'boxes',
  KG: 'kilos',
  KILOS: 'kilos',
  EXPORTADOR: 'exporter',
  EXPORTADORA: 'exporter',
  REGIONDESTINO: 'destination_region',
  PUERTODESTINO: 'destination_port',
  PAISDESTINO: 'destination_country',
  RECIBIDOR: 'receiver',
  NAVE: 'ship',
  TIPONAVE: 'ship_type',
  SEMANA: 'week',
  FECHA: 'etd',
  PUERTOEMBARQUE: 'origin_port',
  FECHAARRIBO: 'eta',
  CODREGORIGEN: 'origin_region_code',
  TEMPORADA: 'season',
};

export const REQUIRED_FIELDS: Field[] = ['exporter', 'kilos', 'season', 'etd', 'variety'];

const NUMBER_FIELDS = new Set<Field>(['boxes', 'kilos', 'week', 'origin_region_code']);
const DATE_FIELDS = new Set<Field>(['etd', 'eta']);
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
// Día 0 de las fechas seriales de Excel.
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);

const headerKey = (value: unknown) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');

const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

// Fecha de Excel: serial, Date o texto (YYYY-MM-DD o DD-MM-YYYY).
export const toIsoDate = (value: unknown): string | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return isoDay(EXCEL_EPOCH + Math.round(value) * MILLISECONDS_PER_DAY);
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return isoDay(
      Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())
    );
  }
  const text = String(value).trim();
  let match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  match = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (match) {
    return `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
  }
  return null;
};

const toNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const toText = (value: unknown): string | null => {
  if (value == null) return null;
  const text = String(value).replace(/\s+/g, ' ').trim();
  return text || null;
};

export type SeasonSummary = {
  season: string;
  rows: number;
  kilos: number;
  exporters: number;
  firstEtd: string | null;
  lastEtd: string | null;
};

export type ParsedVolumeSheet = {
  rows: VolumeRow[];
  missingFields: Field[];
  species: string[];
  seasons: SeasonSummary[];
};

// rows: la hoja como arreglo de filas (sheet_to_json con header: 1). El
// encabezado es la primera fila que trae EXPORTADOR y TEMPORADA; antes puede
// haber filas o columnas vacías.
export const parseVolumeSheet = (sheet: unknown[][]): ParsedVolumeSheet => {
  const headerIndex = sheet.findIndex((row) => {
    const keys = (row || []).map(headerKey);
    return keys.includes('EXPORTADOR') && keys.includes('TEMPORADA');
  });
  if (headerIndex === -1) {
    return { rows: [], missingFields: [...REQUIRED_FIELDS], species: [], seasons: [] };
  }

  const columns = new Map<Field, number>();
  sheet[headerIndex].forEach((cell, index) => {
    const field = HEADERS[headerKey(cell)];
    if (field && !columns.has(field)) columns.set(field, index);
  });
  const missingFields = REQUIRED_FIELDS.filter((field) => !columns.has(field));

  const rows: VolumeRow[] = [];
  const species = new Set<string>();
  const bySeason = new Map<
    string,
    { rows: number; kilos: number; exporters: Set<string>; firstEtd: string | null; lastEtd: string | null }
  >();

  for (let i = headerIndex + 1; i < sheet.length; i++) {
    const cells = sheet[i];
    if (!cells || cells.every((cell) => cell == null || cell === '')) continue;
    // Número de fila del Excel (1 = primera fila), para poder ubicar errores.
    const row = { row_number: i + 1 } as VolumeRow;
    (Object.keys(HEADERS) as string[]).forEach((key) => {
      const field = HEADERS[key];
      if (field in row) return;
      const index = columns.get(field);
      const value = index == null ? null : cells[index];
      if (NUMBER_FIELDS.has(field)) (row[field] as number | null) = toNumber(value);
      else if (DATE_FIELDS.has(field)) (row[field] as string | null) = toIsoDate(value);
      else (row[field] as string | null) = toText(value);
    });
    rows.push(row);

    if (row.species) species.add(row.species);
    const season = row.season ?? '—';
    const summary =
      bySeason.get(season) ||
      { rows: 0, kilos: 0, exporters: new Set<string>(), firstEtd: null, lastEtd: null };
    summary.rows += 1;
    summary.kilos += row.kilos ?? 0;
    if (row.exporter) summary.exporters.add(row.exporter);
    if (row.etd) {
      if (!summary.firstEtd || row.etd < summary.firstEtd) summary.firstEtd = row.etd;
      if (!summary.lastEtd || row.etd > summary.lastEtd) summary.lastEtd = row.etd;
    }
    bySeason.set(season, summary);
  }

  return {
    rows,
    missingFields,
    species: Array.from(species).sort(),
    seasons: Array.from(bySeason, ([season, s]) => ({
      season,
      rows: s.rows,
      kilos: s.kilos,
      exporters: s.exporters.size,
      firstEtd: s.firstEtd,
      lastEtd: s.lastEtd,
    })).sort((a, b) => a.season.localeCompare(b.season)),
  };
};

// Filas con lo mínimo para cargarse (la base las rechaza si falta algo).
export const invalidRows = (rows: VolumeRow[]) =>
  rows.filter((row) => !row.season || row.kilos == null || !row.exporter);

export const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
};
